import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Standard back-office page header. Renders title + subtitle on the start
 * side and an action slot on the end. An optional `toolbar` row sits below
 * (commonly used for search input + filter chips + page-size selector).
 *
 * Props:
 *   title     — string or node
 *   subtitle  — string or node
 *   action    — node (primary CTA), end-aligned
 *   toolbar   — node (search + filters), full-width row beneath
 */
const PageHeader = ({ title, subtitle, action, toolbar }) => (
  <Box
    component="header"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      mb: 3,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Plus Jakarta Sans", "IBM Plex Sans Arabic", Inter, sans-serif',
            fontSize: { xs: 24, sm: 28, md: 32 },
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              color: 'text.secondary',
              maxWidth: '60ch',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {action}
        </Box>
      )}
    </Box>

    {toolbar && (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {toolbar}
      </Box>
    )}
  </Box>
);

export default PageHeader;
